import { useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuthStore } from '@store/authStore';

const SOCKET_URL = (import.meta.env.VITE_API_URL || 'http://localhost:3000').replace(/\/api\/?$/, '');

interface UseRestaurantSocketOptions {
  restaurantId: string;
  role: 'admin' | 'customer';
  onStatusChange?: (data: {
    restaurantId: string;
    status: string;
    previousStatus?: string;
    message: string;
  }) => void;
  onNewOrderRing?: (data: {
    orderId: string;
    orderNumber: string;
    totalAmount: number;
    itemCount: number;
    timestamp: number;
    acknowledged: boolean;
  }) => void;
}

/**
 * useRestaurantSocket - WebSocket hook for restaurant room subscriptions
 *
 * Per Phase 4: WebSocket & Real-Time Sync
 * Subscribes to restaurant admin/customer rooms for real-time updates
 */
export const useRestaurantSocket = (options: UseRestaurantSocketOptions) => {
  const { restaurantId, role, onStatusChange, onNewOrderRing } = options;
  const { tokens } = useAuthStore();
  const socketRef = useRef<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  // Keep callback refs so event listeners always call the latest version of the
  // callbacks without the socket needing to be re-created every render.
  const onStatusChangeRef = useRef(onStatusChange);
  const onNewOrderRingRef = useRef(onNewOrderRing);
  useEffect(() => { onStatusChangeRef.current = onStatusChange; }, [onStatusChange]);
  useEffect(() => { onNewOrderRingRef.current = onNewOrderRing; }, [onNewOrderRing]);

  useEffect(() => {
    const accessToken = tokens?.access_token;

    if (!restaurantId || !accessToken) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setIsConnected(false);
      return;
    }

    if (socketRef.current) {
      socketRef.current.auth = { token: accessToken };
      if (!socketRef.current.connected) {
        socketRef.current.connect();
      }
      return;
    }

    const socket = io(`${SOCKET_URL}/events`, {
      auth: { token: accessToken },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 10,
    });

    socketRef.current = socket;

    const handleConnect = () => {
      setIsConnected(true);
      socket.emit('subscribeToRestaurant', { restaurantId, role });
    };

    const handleDisconnect = () => { setIsConnected(false); };
    const handleConnectError = () => { setIsConnected(false); };

    // Stable wrappers that always delegate to the latest callback ref.
    const handleStatusChange = (data: any) => { onStatusChangeRef.current?.(data); };
    const handleNewOrderRing = (data: any) => { onNewOrderRingRef.current?.(data); };

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('connect_error', handleConnectError);
    socket.on('store_status_changed', handleStatusChange);
    if (role === 'admin') {
      socket.on('new_order_ring', handleNewOrderRing);
    }

    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('connect_error', handleConnectError);
      socket.off('store_status_changed', handleStatusChange);
      socket.off('new_order_ring', handleNewOrderRing);
      socket.emit('unsubscribeFromRestaurant', { restaurantId, role });
      socket.disconnect();
      socketRef.current = null;
      setIsConnected(false);
    };
  }, [restaurantId, role, tokens?.access_token]);

  return {
    socket: socketRef.current,
    isConnected,
  };
};
